import { createSelector } from '@reduxjs/toolkit';
import { groupBy } from 'lodash-es';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { arrayToMap } from 'utils/arrayToMap';
import { getProjectsActivityFilterCacheKey } from '../utils/getProjectsActivityFilterCacheKey';
import { selectShortTermProjectsSearchFilters } from './shortTermProjects.selectors';
import {
  getUserProjectPreferenceKey,
  isProjectHiddenByPreferenceOrAncestor,
} from '../utils/userProjectPreferences';

const selectProjectsState = (state) => state.projects;

export const selectProjects = createSelector(
  [selectProjectsState],
  (state) => state.projects?.entities || {}
);

export const selectProjectsOfBusiness = createSelector(
  [selectProjectsState],
  (state) => state.projectsOfBusiness.entities
);

export const selectProjectsFolder = createSelector(
  [selectProjectsState],
  (state) => state.folder
);

export const selectSearchFilter = createSelector(
  [selectProjectsState],
  (state) => state.searchFilter
);

export const selectSearchFilterActive = createSelector(
  [selectProjectsState],
  (state) => (state.searchFilter ? true : false)
);

export const PROJECTS_ACTIVITY_FILTER_MODES = {
  LATEST: 'USER_ACTIVITY_LAST_30_DAYS',
  ALL: 'ALL_PROJECTS',
  VEROTTAJAILMOITETTAVA: 'VEROTTAJAILMOITETTAVA_PROJECTS',
};

export const selectProjectsActivityFilterMode = createSelector(
  [selectProjectsState],
  (state) =>
    state.projectsActivityFilterMode ||
    (state.showAll
      ? PROJECTS_ACTIVITY_FILTER_MODES.ALL
      : PROJECTS_ACTIVITY_FILTER_MODES.LATEST)
);

export const selectShowAll = createSelector(
  [selectProjectsActivityFilterMode],
  (projectsActivityFilterMode) =>
    projectsActivityFilterMode === PROJECTS_ACTIVITY_FILTER_MODES.ALL
);

export const selectShowShortTermProjects = createSelector(
  [selectProjectsState],
  (state) => state.showShortTermProjects
);

export const selectSelectedShortTermProjectsViewMode = createSelector(
  [selectProjectsState],
  (state) => state.selectedShortTermProjectsViewMode
);

export const selectMobileShowList = createSelector(
  [selectProjectsState],
  (state) => state.mobileShowList
);

export const selectProjectsIds = createSelector(
  [selectProjectsState],
  (state) => state.projects?.ids || []
);

export const selectSelectedProjectId = createSelector(
  [selectProjectsState],
  (state) => state.selectedProjectId
);

export const selectIsProjectsLoading = createSelector(
  [selectProjectsState],
  (state) => state.loading
);

export const selectUserProjectsLoaded = createSelector(
  [selectProjectsState],
  (state) => state.userProjectsLoaded
);

export const selectProjectsActivityFilterCacheKey = createSelector(
  [selectUserActiveEmployerInfo],
  (activeEmployerInfo) => getProjectsActivityFilterCacheKey(activeEmployerInfo)
);

export const selectIsLoadingProjectsActivityFilter = createSelector(
  [
    selectProjectsState,
    selectProjectsActivityFilterMode,
    selectProjectsActivityFilterCacheKey,
  ],
  (state, projectsActivityFilterMode, cacheKey) => {
    if (
      !cacheKey ||
      projectsActivityFilterMode === PROJECTS_ACTIVITY_FILTER_MODES.ALL
    ) {
      return false;
    }

    if (
      projectsActivityFilterMode ===
      PROJECTS_ACTIVITY_FILTER_MODES.VEROTTAJAILMOITETTAVA
    ) {
      const sourceStatuses =
        state.verottajailmoitettavaProjectIdsRequestStatusByEmployer?.[
          cacheKey
        ];

      if (!sourceStatuses) {
        return false;
      }

      const statuses = Object.values(sourceStatuses);

      // Hide skeleton as soon as any source returns — partial results render
      // immediately while slower sources keep loading in the background.
      if (statuses.some((status) => status === 'fulfilled')) {
        return false;
      }

      return statuses.some((status) => status === 'pending');
    }

    return (
      state.latestUserActiveProjectIdsRequestStatusByEmployer?.[cacheKey] ===
      'pending'
    );
  }
);

export const selectIsLoadingMoreVerottajailmoitettavaProjects = createSelector(
  [
    selectProjectsState,
    selectProjectsActivityFilterMode,
    selectProjectsActivityFilterCacheKey,
  ],
  (state, projectsActivityFilterMode, cacheKey) => {
    if (
      !cacheKey ||
      projectsActivityFilterMode !==
        PROJECTS_ACTIVITY_FILTER_MODES.VEROTTAJAILMOITETTAVA
    ) {
      return false;
    }

    const sourceStatuses =
      state.verottajailmoitettavaProjectIdsRequestStatusByEmployer?.[cacheKey];

    if (!sourceStatuses) {
      return false;
    }

    const statuses = Object.values(sourceStatuses);
    const anyFulfilled = statuses.some((status) => status === 'fulfilled');
    const anyPending = statuses.some((status) => status === 'pending');

    return anyFulfilled && anyPending;
  }
);

export const selectIsAddingEmployer = createSelector(
  [selectProjectsState],
  (state) => state.isAddingEmployer
);

export const selectProjectIdsLatestClockedInTo = createSelector(
  [selectProjectsState],
  (state) => state.latestClockedInProjectIds
);
export const selectProjectRecordHours = createSelector(
  [selectProjectsState],
  (state) => state.projectRecordHours
);

export const selectIsProjectRecordHoursLoading = createSelector(
  [selectProjectsState],
  (state) => state.projectRecordHoursLoading
);

export const selectLatestUserActiveProjectIds = createSelector(
  [selectProjectsState, selectProjectsActivityFilterCacheKey],
  (state, cacheKey) =>
    (cacheKey && state.latestUserActiveProjectIdsByEmployer?.[cacheKey]) || []
);

const selectVerottajailmoitettavaProjectIdsByEmployerMap = createSelector(
  [selectProjectsState],
  (state) => state.verottajailmoitettavaProjectIdsByEmployer
);

export const selectVerottajailmoitettavaProjectIds = createSelector(
  [
    selectVerottajailmoitettavaProjectIdsByEmployerMap,
    selectProjectsActivityFilterCacheKey,
  ],
  (mapByEmployer, cacheKey) => {
    if (!cacheKey) {
      return [];
    }

    const sources = mapByEmployer?.[cacheKey];

    if (!sources) {
      return [];
    }

    return Array.from(
      new Set(
        Object.values(sources).reduce(
          (acc, projectIds) => acc.concat(projectIds || []),
          []
        )
      )
    );
  }
);
export const selectSavingProcountorDimension = createSelector(
  [selectProjectsState],
  (state) => state.savingProcountorDimension
);

export const selectIsProjectCreateLoading = createSelector(
  [selectProjectsState],
  (state) => state.isProjectCreateLoading
);

export const selectUserProjectPreferences = createSelector(
  [selectProjectsState],
  (state) => state.userProjectPreferences || {}
);

export const selectUserProjectPreferencesLoaded = createSelector(
  [selectProjectsState],
  (state) => state.userProjectPreferencesLoaded
);

export const selectUserProjectPreferencesLoading = createSelector(
  [selectProjectsState],
  (state) => state.userProjectPreferencesLoading
);

export const selectShowHiddenProjects = createSelector(
  [selectProjectsState],
  (state) => Boolean(state.showHiddenProjects)
);

export const selectShowArchivedProjects = createSelector(
  [selectProjectsState],
  (state) => Boolean(state.showArchivedProjects)
);

export const selectHasHiddenProjectPreferences = createSelector(
  [selectUserProjectPreferences],
  (preferences) => Object.values(preferences).some((preference) => preference?.hidden)
);

export const selectProjectPreferenceByProject = (project) =>
  createSelector([selectUserProjectPreferences], (preferences) =>
    preferences[getUserProjectPreferenceKey(project)] || null
  );

export const selectIsProjectFavorite = (project) =>
  createSelector([selectProjectPreferenceByProject(project)], (preference) =>
    Boolean(preference?.favorite)
  );

export const selectIsProjectHidden = (project) =>
  createSelector([selectProjectPreferenceByProject(project)], (preference) =>
    Boolean(preference?.hidden)
  );

export const selectBusinessIntegrationPartners = createSelector(
  [selectProjectsState],
  (state) => state.businessIntegrationPartners
);

export const selectIsUpdatingProjectStatus = createSelector(
  [selectProjectsState],
  (state) => state.isUpdatingProjectStatus
);

export const selectProjectActivityFromDate = createSelector(
  [selectProjectsState],
  (state) => state.projectActivityFromDate
);

export const selectProjectActivityToDate = createSelector(
  [selectProjectsState],
  (state) => state.projectActivityToDate
);

export const selectIsLoadingProjectActivity = createSelector(
  [selectProjectsState],
  (state) => state.isLoadingProjectActivity
);

export const selectProjectActivityData = createSelector(
  [selectProjectsState],
  (state) => state.projectActivityData
);

export const selectProjectFeatureStatusItems = createSelector(
  [selectProjectsState],
  (state) => state.projectFeatureStatusItems
);

export const selectSelectedStatusBasedProjectsMode = createSelector(
  [selectProjectsState],
  (state) => state.selectedStatusBasedProjectsMode
);

export const selectIsLoadingArchivedProjects = createSelector(
  [selectProjectsState],
  (state) => state.isLoadingArchivedProjects
);

export const selectProjectFeatureStatusItemsWithErrorStatus = createSelector(
  [selectProjectFeatureStatusItems],
  (statusItems) => statusItems.filter((item) => item.status === 'ERROR')
);

export const selectProjectFeatureErrorStatusItemsGroupedByProjectId =
  createSelector(
    [selectProjectFeatureStatusItemsWithErrorStatus],
    (projectStatusItems) => groupBy(projectStatusItems, 'projectId')
  );

export const selectSelectedProjectFeatureItemsWithErrorStatus = createSelector(
  [
    selectProjectFeatureErrorStatusItemsGroupedByProjectId,
    selectSelectedProjectId,
  ],
  (statusItems, selectedProjectId) =>
    statusItems[selectedProjectId] ? statusItems[selectedProjectId] : []
);

export const selectSelectedProjectEmployeeReportError = createSelector(
  [selectSelectedProjectFeatureItemsWithErrorStatus],
  (statusItems) =>
    statusItems.find((item) => item.feature === 'EMPLOYEE_REPORT')
);

export const selectProjectActivityProjectIds = createSelector(
  [selectProjectActivityData],
  (projectActivityData) => Object.keys(projectActivityData)
);

export const selectProjectsOfBusinessArray = createSelector(
  [selectProjectsOfBusiness],
  (projects) => Object.values(projects).filter((project) => !project.deletedAt)
);

export const selectAllProjectsOfBusiness = createSelector(
  [selectProjectsOfBusinessArray],
  (projects) => {
    return [...projects].sort((a, b) => {
      // Sort by name alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });
  }
);

export const selectProjectsArray = createSelector(
  [selectProjects],
  (projects) => Object.values(projects).filter((project) => !project.deletedAt)
);

export const selectNormalProjectsArray = createSelector(
  [selectProjectsArray],
  (projects) => projects.filter((project) => project.type === 'NORMAL')
);

export const selectShortTermProjects = createSelector(
  [selectProjectsArray],
  (projects) => projects.filter((project) => project.type === 'SHORT_TERM')
);

export const selectEmployerTypeProjects = createSelector(
  [selectProjectsArray],
  (projects) =>
    projects.filter((project) => project.businessTypeEnum === 'EMPLOYER')
);

export const selectProjectEmployeeEmployerProjects = createSelector(
  [selectProjectsState],
  (state) => state.projectEmployeeEmployerProjects.entities
);

export const selectProjectEmployeeEmployerProjectsArray = createSelector(
  [selectProjectEmployeeEmployerProjects],
  (employerProjects) => Object.values(employerProjects)
);

export const selectProjectsIndexedByParent = createSelector(
  [selectProjects],
  (projects) => groupBy(projects, 'parent')
);

export const selectChildProjectsOfSelectedProject = createSelector(
  [selectSelectedProjectId, selectProjectsIndexedByParent],
  (selectedProjectId, projectsIndexedByParent) =>
    projectsIndexedByParent[selectedProjectId]
      ? projectsIndexedByParent[selectedProjectId]
      : []
);

export const selectNormalProjectsArrayWithJoinedData = createSelector(
  [selectNormalProjectsArray, selectProjects],
  (projectsArray, projects) => {
    const joinedDataProjects = projectsArray.map((project) => ({
      ...project,
      masterParent: projects[project.masterParent]
        ? projects[project.masterParent]
        : { id: project.masterParent },
      parent: projects[project.parent]
        ? projects[project.parent]
        : { id: project.parent },
      employer: projects[project.employer]
        ? projects[project.employer]
        : { id: project.employer },
    }));
    return joinedDataProjects;
  }
);

export const selectProjectsArrayWithJoinedData = createSelector(
  [selectProjectsArray, selectProjects],
  (projectsArray, projects) => {
    const joinedDataProjects = projectsArray.map((project) => ({
      ...project,
      masterParent: projects[project.masterParent]
        ? projects[project.masterParent]
        : { id: project.masterParent },
      parent: projects[project.parent]
        ? projects[project.parent]
        : { id: project.parent },
      employer: projects[project.employer]
        ? projects[project.employer]
        : { id: project.employer },
    }));
    return joinedDataProjects;
  }
);

const expandProjectIdsWithAncestors = (projects, projectIds = []) => {
  const projectsById = arrayToMap(projects);
  const expandedIds = new Set(projectIds);

  projectIds.forEach((projectId) => {
    let currentProject = projectsById[projectId];

    while (currentProject?.parent?.id) {
      const parentId = currentProject.parent.id;

      if (expandedIds.has(parentId)) {
        break;
      }

      expandedIds.add(parentId);
      currentProject = projectsById[parentId];
    }
  });

  return [...expandedIds];
};

const appendEmployerProjects = (projects = []) =>
  Object.values(
    arrayToMap([
      ...projects,
      ...projects
        .filter((project) => project.masterParent?.id)
        .map((project) => project.masterParent),
    ])
  );

export const selectLatestUserActiveProjectIdsWithAncestors = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectLatestUserActiveProjectIds,
    selectUserProjectPreferences,
    selectProjects,
  ],
  (projects, latestActiveProjectIds, userProjectPreferences, projectsById) =>
    expandProjectIdsWithAncestors(
      projects,
      latestActiveProjectIds.filter((projectId) => {
        const project = projectsById[projectId];

        return (
          project &&
          !isProjectHiddenByPreferenceOrAncestor(
            project,
            userProjectPreferences,
            projectsById
          )
        );
      })
    )
);

// export const selectedProjectsArraySortedByName = createSelector(
//   [selectProjectsArrayWithJoinedData],
//   (projects) => {
//     return [...projects].sort((a, b) => {
//       return (a.name || '').localeCompare(b.name || '');
//     });
//   });

export const selectLatestUserActiveProjects = createSelector(
  [selectProjectsArrayWithJoinedData, selectLatestUserActiveProjectIdsWithAncestors],
  (projects, latestActiveProjectIds) =>
    projects.filter((project) => latestActiveProjectIds.includes(project.id))
);

export const selectLatestUserActiveProjectsWithEmployers = createSelector(
  [selectLatestUserActiveProjects],
  (latestProjects) => appendEmployerProjects(latestProjects)
);

export const selectLatestUserActiveProjectsAndEmployerProjectsIds =
  createSelector(
    [selectLatestUserActiveProjectsWithEmployers],
    (latestProjects) => latestProjects.map((project) => project.id)
  );

export const selectLatestUserActiveProjectsAndEmployerProjectsIdsSet =
  createSelector(
    [selectLatestUserActiveProjectsWithEmployers],
    (latestProjects) => new Set(latestProjects.map((project) => project.id))
  );

export const selectVerottajailmoitettavaProjectIdsWithAncestors =
  createSelector(
    [selectProjectsArrayWithJoinedData, selectVerottajailmoitettavaProjectIds],
    (projects, verottajailmoitettavaProjectIds) =>
      expandProjectIdsWithAncestors(projects, verottajailmoitettavaProjectIds)
  );

export const selectVerottajailmoitettavaProjects = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectVerottajailmoitettavaProjectIdsWithAncestors,
  ],
  (projects, verottajailmoitettavaProjectIds) =>
    projects.filter((project) => verottajailmoitettavaProjectIds.includes(project.id))
);

export const selectVerottajailmoitettavaProjectsWithEmployers = createSelector(
  [selectVerottajailmoitettavaProjects],
  (verottajailmoitettavaProjects) =>
    appendEmployerProjects(verottajailmoitettavaProjects)
);

export const selectVerottajailmoitettavaProjectsAndEmployerProjectsIdsSet =
  createSelector(
    [selectVerottajailmoitettavaProjectsWithEmployers],
    (verottajailmoitettavaProjects) =>
      new Set(verottajailmoitettavaProjects.map((project) => project.id))
  );

export const selectProjectsEntitiesWithJoinedData = createSelector(
  [selectProjectsArrayWithJoinedData],
  (projects) => arrayToMap(projects)
);

export const selectSelectedProject = createSelector(
  [selectProjects, selectSelectedProjectId],
  (projects, selectedProjectId) =>
    projects[selectedProjectId] ? projects[selectedProjectId] : {}
);

export const selectSelectedProjectEmployer = createSelector(
  [selectEmployerTypeProjects, selectSelectedProject],
  (employerProjects, selectedProject) =>
    employerProjects.find(
      (employerProject) =>
        employerProject.business_id === selectedProject.business_id
    )
);

export const selectNormalProjectsOfSelectedBusiness = createSelector(
  [selectNormalProjectsArray, selectSelectedProject],
  (projects, selectedProject) =>
    projects.filter(
      (project) => project.business_id === selectedProject.business_id
    )
);

export const selectProjectById = (projectId) =>
  createSelector([selectProjects], (projects) => projects[projectId] || {});

export const selectIsSelectedProjectEmployerProject = createSelector(
  [selectSelectedProject],
  (project) => {
    const isEmployerProject =
      project.businessTypeEnum && project.businessTypeEnum === 'EMPLOYER';

    return isEmployerProject;
  }
);

export const selectSelectedBusinessShortTermProjects = createSelector(
  [selectSelectedProject, selectShortTermProjects],
  (selectedProject, shortTermProjects) =>
    shortTermProjects.filter(
      (shortTermProject) =>
        shortTermProject.business_id === selectedProject.business_id
    )
);

export const selectSearchFilteredShortTermProjects = createSelector(
  [
    selectSelectedBusinessShortTermProjects,
    selectShortTermProjectsSearchFilters,
  ],
  (shortTermProjects, searchFilters) => {
    const filteredProjects = shortTermProjects.filter((shortTermProject) => {
      const matchingName = shortTermProject.name
        .toLowerCase()
        .includes(searchFilters['name'].toLowerCase());

      const customerName = shortTermProject.customerName || '';

      const matchingCustomer = customerName
        .toLowerCase()
        .includes(searchFilters['customer'].toLowerCase());

      const orderNumber = shortTermProject.orderNumber?.toString() || '';

      const matchingOrderNumber = orderNumber.includes(
        searchFilters['orderNumber']
      );

      const projectAddress = shortTermProject.street1 || '';

      const matchingAddress = projectAddress
        .toLowerCase()
        .includes(searchFilters['address']);

      if (
        matchingName &&
        matchingCustomer &&
        matchingOrderNumber &&
        matchingAddress
      ) {
        return true;
      } else {
        return false;
      }
    });

    return filteredProjects;
  }
);

export const selectSelectedProjectWithJoinedData = createSelector(
  [selectProjectsEntitiesWithJoinedData, selectSelectedProjectId],
  (projects, selectedProjectId) =>
    projects[selectedProjectId] ? projects[selectedProjectId] : {}
);

export const selectSelectedProjectParent = createSelector(
  [selectProjects, selectSelectedProject],
  (projects, selectedProject) =>
    projects[selectedProject.parent] ? projects[selectedProject.parent] : {}
);

export const selectSelectedProjectMasterParent = createSelector(
  [selectProjects, selectSelectedProject],
  (projects, selectedProject) =>
    projects[selectedProject.masterParent]
      ? projects[selectedProject.masterParent]
      : {}
);

export const selectSelectedProjectRecordHourTotals = createSelector(
  [selectProjectRecordHours, selectSelectedProjectId],
  (projectRecordHours, selectedProjectId) =>
    projectRecordHours[selectedProjectId]
      ? projectRecordHours[selectedProjectId]
      : {}
);

export const selectProcountorDimensions = createSelector(
  [selectProjectsState],
  (projectsState) => projectsState.procountorDimensions
);

export const selectProjectMergeStatus = createSelector(
  [selectProjectsState],
  (state) => state.projectMergeStatus
);

export const selectProjectsMerging = createSelector(
  [selectProjectsState],
  (state) => state.projectsMerging
);

export const selectAddingProjectToMerge = createSelector(
  [selectProjectsState],
  (state) => state.addingProjectToMerge
);

export const selectProjectByKey = createSelector(
  [selectProjectsArray, (_, projectKey) => projectKey],
  (projects, projectKey) =>
    projects.find((project) => project.projectKey === projectKey)
);

export const selectSelectedProjectIntegrationPartners = createSelector(
  [selectSelectedProject, selectBusinessIntegrationPartners],
  (selectedProject, businessIntegrationPartners) => {
    return businessIntegrationPartners[selectedProject.business_id]
      ? businessIntegrationPartners[selectedProject.business_id]
      : [];
  }
);

export const selectSelectedEmployerBusinessNormalProjects = createSelector(
  [selectProjectsArray, selectSelectedProject],
  (projects, selectedProject) => {
    return projects.filter(
      (project) =>
        project.business_id === selectedProject.business_id &&
        project.type !== 'SHORT_TERM'
    );
  }
);
